type GtagEventParams = Record<string, unknown>;

type GtagFunction = {
  (command: "js", date: Date): void;
  (command: "config", targetId: string, params?: GtagEventParams): void;
  (command: "event", eventName: string, params?: GtagEventParams): void;
};

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: GtagFunction;
  }
}

const SCRIPT_ID = "google-ads-gtag-js";
const SIGNAL_KEY_PREFIX = "google_ads:signal_sent";

type GoogleAdsPreferenceConfig = {
  googleAdsConversionId?: string | null;
  googleAdsConversionLabel?: string | null;
};

const normalizeConversionId = (conversionId?: string | null): string | null => {
  if (!conversionId) {
    return null;
  }

  const normalized = conversionId.trim().toUpperCase();
  if (!normalized) {
    return null;
  }

  return normalized.startsWith("AW-") ? normalized : `AW-${normalized}`;
};

const hasCookieConsent = (): boolean => {
  try {
    return window.localStorage.getItem("cookiesAccepted") === "true";
  } catch {
    return false;
  }
};

const ensureGtagBootstrap = (): GtagFunction | null => {
  if (typeof window === "undefined") {
    return null;
  }

  window.dataLayer = window.dataLayer || [];

  if (!window.gtag) {
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer.push(args);
    } as GtagFunction;
    window.gtag("js", new Date());
  }

  return window.gtag;
};

const ensureScript = (conversionId: string): void => {
  if (typeof document === "undefined") {
    return;
  }

  const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
  if (existing) {
    return;
  }

  const script = document.createElement("script");
  script.id = SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(conversionId)}`;
  document.head.appendChild(script);
};

const normalizeCurrencyCode = (value?: string | null): "BRL" | "USD" | "EUR" => {
  const normalized = (value ?? "BRL").trim().toUpperCase();
  if (normalized === "USD" || normalized === "EUR") {
    return normalized;
  }

  return "BRL";
};

const markTracked = (key: string): void => {
  try {
    window.localStorage.setItem(key, "1");
  } catch {
    // Ignore storage failures.
  }
};

const wasTracked = (key: string): boolean => {
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};

export const initializeGoogleAdsTracking = (config?: GoogleAdsPreferenceConfig | null): void => {
  const conversionId = normalizeConversionId(config?.googleAdsConversionId);
  if (!conversionId || !hasCookieConsent()) {
    return;
  }

  const gtag = ensureGtagBootstrap();
  if (!gtag) {
    return;
  }

  ensureScript(conversionId);
  gtag("config", conversionId);
};

type TrackOrderCreatedSignalParams = {
  config?: GoogleAdsPreferenceConfig | null;
  transactionId: string;
  value: number;
  currencyCode?: string | null;
};

export const trackGoogleAdsOrderCreatedSignal = ({
  config,
  transactionId,
  value,
  currencyCode
}: TrackOrderCreatedSignalParams): void => {
  const conversionId = normalizeConversionId(config?.googleAdsConversionId);
  if (!conversionId || !transactionId || !hasCookieConsent()) {
    return;
  }

  const trackingKey = `${SIGNAL_KEY_PREFIX}:${conversionId}:order_created:${transactionId}`;
  if (wasTracked(trackingKey)) {
    return;
  }

  const gtag = ensureGtagBootstrap();
  if (!gtag) {
    return;
  }

  ensureScript(conversionId);
  gtag("config", conversionId);

  const normalizedValue = Number.isFinite(value) ? Number(value.toFixed(2)) : 0;
  const normalizedCurrency = normalizeCurrencyCode(currencyCode);

  gtag("event", "begin_checkout", {
    value: normalizedValue,
    currency: normalizedCurrency,
    transaction_id: transactionId
  });

  markTracked(trackingKey);
};
